'use client';

import React, { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Wifi, Shield, BookOpen, X, Zap, TrendingUp, AlertTriangle, CheckCircle, ArrowRight, Target, Clock, BarChart2, Tag } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';



function HowItWorksModal({ onClose }: { onClose: () => void }) {
  const { t, lang } = useLanguage();
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-[#0d0d18] border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0d0d18] border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/20 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">{t('modalTitle')}</h2>
              <p className="text-[11px] text-zinc-500 font-mono">{t('modalSub')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-900 border border-white/5 hover:bg-zinc-800 transition-colors"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">

          {/* Section 1 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest">{t('section1Title')}</h3>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">{t('section1Intro')}</p>
            <div className="space-y-3">
              {[
                { icon: BarChart2, label: t('trendLabel'), desc: t('trendDesc') },
                { icon: TrendingUp, label: t('momentumLabel'), desc: t('momentumDesc') },
                { icon: Target, label: t('entryLabel'), desc: t('entryDesc') },
                { icon: Clock, label: t('sessionLabel'), desc: t('sessionDesc') },
                { icon: AlertTriangle, label: t('riskLabel'), desc: t('riskDesc') },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="flex gap-3 p-3 rounded-xl bg-zinc-900/60 border border-white/5">
                  <div className="mt-0.5 w-7 h-7 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-200 mb-0.5">{label}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5" />

          {/* Section 2 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">{t('section2Title')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'BUY', color: 'emerald', desc: t('buyDesc') },
                { label: 'SELL', color: 'red', desc: t('sellDesc') },
                { label: 'WAIT', color: 'amber', desc: t('waitDesc') },
              ].map(({ label, color, desc }) => (
                <div key={label} className={`p-3 rounded-xl bg-${color}-500/5 border border-${color}-500/15`}>
                  <span className={`text-xs font-bold text-${color}-400 font-mono`}>{label}</span>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5" />

          {/* Section 3 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-widest">{t('section3Title')}</h3>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              {t('section3Intro')} <span className="text-white font-medium">{t('section3IntroNot')}</span> {t('section3IntroEnd')}
            </p>
            <ol className="space-y-3">
              {[
                { step: '01', title: t('step1Title'), desc: t('step1Desc') },
                { step: '02', title: t('step2Title'), desc: t('step2Desc') },
                { step: '03', title: t('step3Title'), desc: t('step3Desc') },
                { step: '04', title: t('step4Title'), desc: t('step4Desc') },
                { step: '05', title: t('step5Title'), desc: t('step5Desc') },
                { step: '06', title: t('step6Title'), desc: t('step6Desc') },
              ].map(({ step, title, desc }) => (
                <li key={step} className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-zinc-800 border border-white/5 flex items-center justify-center text-[10px] font-bold text-amber-400 font-mono">{step}</span>
                  <div>
                    <p className="text-xs font-semibold text-zinc-200 mb-0.5">{title}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Disclaimer */}
          <div className="flex gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-400 leading-relaxed">
              <span className="text-amber-400 font-semibold">{t('disclaimer')}</span>
              {t('disclaimerText')}
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

export default function DashboardTopbar() {
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const { lang, toggleLang, t } = useLanguage();

  return (
    <>
      <header className="xau-header sticky top-0 z-50 w-full border-b border-white/5 bg-[#09090f]/95 backdrop-blur-sm">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            {/* Logo + Brand */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <AppLogo size={28} />
              <div className="flex flex-col">
                <span className="font-semibold text-xs sm:text-sm tracking-tight gold-text leading-none">
                  XAU ENGINE AI PRO
                </span>
                <span className="hidden sm:block text-[10px] text-zinc-500 font-mono tracking-widest uppercase leading-none mt-0.5">
                  {t('brandSub')}
                </span>
              </div>
            </div>

            {/* Center — Pair + Timeframe */}
            <div className="hidden md:flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900 border border-white/5">
                <div className="w-2 h-2 rounded-full bg-emerald-400 pulse-gold" />
                <span className="text-xs font-mono text-zinc-300 tracking-widest">XAU/USD</span>
                <span className="text-xs font-mono text-zinc-500">·</span>
                <span className="text-xs font-mono text-amber-400 font-semibold">M15</span>
                <span className="text-xs font-mono text-zinc-500">·</span>
                <span className="text-xs font-mono text-zinc-400">{t('pairLabel')}</span>
              </div>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Live indicator */}
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                <Wifi className="w-3 h-3 text-emerald-400" />
                <span className="text-[11px] font-mono text-emerald-400 font-medium">{t('live')}</span>
              </div>

              {/* Security badge */}
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-900 border border-white/5">
                <Shield className="w-3 h-3 text-zinc-500" />
                <span className="text-[11px] text-zinc-500 font-mono">{t('secure')}</span>
              </div>

              {/* How It Works button */}
              <button
                onClick={() => setShowHowItWorks(true)}
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 transition-all duration-150"
              >
                <BookOpen className="w-3 h-3 text-zinc-500" />
                <span className="text-[11px] text-zinc-400 font-mono">{t('howItWorks')}</span>
              </button>

              {/* Language toggle */}
              <button
                onClick={toggleLang}
                title={lang === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 transition-all duration-150 active:scale-95"
              >
                <span className="text-[11px] font-mono font-semibold text-zinc-300 tracking-widest">
                  {lang === 'en' ? '🇺🇸 EN' : '🇪🇸 ES'}
                </span>
              </button>

              {/* Nav links */}
              <div className="flex items-center gap-1.5">
                <Link
                  href="/pricing"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/10 hover:bg-[#d4af37]/20 text-[#d4af37] text-xs font-semibold transition-all duration-150 active:scale-95"
                >
                  <Tag className="w-3 h-3" />
                  {lang === 'en' ? 'Upgrade' : 'Mejorar'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* How It Works Modal */}
      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}
    </>
  );
}